// 모사 데이터 프리셋 — 순수 로직(저장분 추출·적용 의미론·plan 축 제외).
//
// 결정 기록: docs/decisions/2026-09-03-composer-simulation-presets.md.
//
// ## plan 축은 프리셋에 저장하지 않는다
//
// 계획(`plan`) 출처 축(분할 그룹)의 값은 **그 점검·그 시점**의 산출물(행 id)이라 템플릿 자산으로
// 박제하면 반드시 썩는다 — 다음 점검에 실으면 서버가 "분할 대상에 없는 항목입니다"로 400을 낸다.
// 그래서 저장(추출)과 적용(주입) 양쪽이 이 모듈의 같은 판정(`presetExcludedAxisKeys`)을 거친다.
// split 예약 키는 축 목록에 없어도(정책을 껐다 켠 사이 등) 항상 제외한다.
//
// ## 적용은 병합이 아니라 **대체**다
//
// 프리셋 이름은 "이 벌을 고르면 이 상태"라는 약속이다 — 기존 즉석 행 위에 병합하면 프리셋에 없는
// 축이 남아 "프리셋+α" 상태가 되고, 이름이 뜻하는 화면과 실제 화면이 어긋난다. 단 plan 축 값은
// 프리셋 소유가 아니므로(위 제외 규칙) 대체 대상이 아니다 — 현재 값을 그대로 유지한다.

import {
  PREVIEW_SIMULATION_SPLIT_AXIS_KEY,
  type PreviewSimulationAxis,
  type SimulationPreset,
} from '@safetyhub/shared';
import { isPlanSourcedAxis } from './previewSimulationAxes';

/** 프리셋이 소유할 수 없는 축 key 집합 — plan 출처 축 전부 + split 예약 키(무조건). */
function presetExcludedAxisKeys(axes: PreviewSimulationAxis[]): Set<string> {
  const keys = new Set(axes.filter(isPlanSourcedAxis).map((a) => a.key));
  keys.add(PREVIEW_SIMULATION_SPLIT_AXIS_KEY);
  return keys;
}

/** 이 축을 프리셋에 담을 수 있는가 — 프리셋 편집 UI의 후보 필터. */
export function isPresetStorableAxis(axis: PreviewSimulationAxis): boolean {
  return !isPlanSourcedAxis(axis) && axis.key !== PREVIEW_SIMULATION_SPLIT_AXIS_KEY;
}

/**
 * 현재 모사 값 → 프리셋 저장분(values/labels) 추출 — plan 축 제외.
 * labels는 값이 실린 키만 남긴다(값 없는 라벨은 프리셋을 거짓말쟁이로 만든다).
 */
export function extractPresetPayload(
  simulation: Record<string, unknown> | undefined,
  labels: Record<string, string> | undefined,
  axes: PreviewSimulationAxis[],
): Pick<SimulationPreset, 'values' | 'labels'> {
  const excluded = presetExcludedAxisKeys(axes);
  const values: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(simulation ?? {})) {
    if (value === undefined) continue;
    if (excluded.has(key)) continue;
    values[key] = value;
  }
  const kept: Record<string, string> = {};
  for (const [key, label] of Object.entries(labels ?? {})) {
    if (values[key] !== undefined) kept[key] = label;
  }
  return Object.keys(kept).length > 0 ? { values, labels: kept } : { values };
}

/**
 * 프리셋 적용 — **대체 의미론**(파일 헤더 참조): plan 축 값만 유지하고 나머지는 프리셋 벌로
 * 갈아 끼운다. 반환 labels는 유지된 plan 축 라벨 + 프리셋 라벨의 전체 맵이다.
 */
export function applyPresetToSimulation(
  current: Record<string, unknown> | undefined,
  currentLabels: Record<string, string> | undefined,
  preset: SimulationPreset,
  axes: PreviewSimulationAxis[],
): {
  simulation: Record<string, unknown> | undefined;
  labels: Record<string, string>;
} {
  const excluded = presetExcludedAxisKeys(axes);
  const next: Record<string, unknown> = {};
  const nextLabels: Record<string, string> = {};
  // plan 축(적용 시점 선택 값)은 프리셋 소유가 아니다 — 현재 값·라벨 유지.
  for (const [key, value] of Object.entries(current ?? {})) {
    if (!excluded.has(key) || value === undefined) continue;
    next[key] = value;
    const label = currentLabels?.[key];
    if (label) nextLabels[key] = label;
  }
  for (const [key, value] of Object.entries(preset.values)) {
    // 방어: 손으로 오염된(또는 규칙 도입 전) 프리셋의 plan 축 값은 무시 — 썩은 행 id 주입 차단.
    if (excluded.has(key)) continue;
    if (value === undefined) continue;
    next[key] = value;
    const label = preset.labels?.[key];
    if (label) nextLabels[key] = label;
  }
  return {
    simulation: Object.keys(next).length > 0 ? next : undefined,
    labels: nextLabels,
  };
}

/**
 * 프리셋 적용 해제 — 적용(대체)의 역: 프리셋이 소유할 수 있는(storable) 축을 전부 걷어내고
 * plan 축 값·라벨만 남긴다. `applyPresetToSimulation(current, labels, 빈 프리셋, axes)`과
 * 동치지만, "해제"라는 의도를 이름이 말하게 별도로 둔다(2026-09-04 — 적용/해제가 속성 패널로
 * 이동하면서 해제가 독립 조작이 됐다).
 */
export function clearPresetStorableSimulation(
  current: Record<string, unknown> | undefined,
  currentLabels: Record<string, string> | undefined,
  axes: PreviewSimulationAxis[],
): {
  simulation: Record<string, unknown> | undefined;
  labels: Record<string, string>;
} {
  return applyPresetToSimulation(
    current,
    currentLabels,
    { id: '', name: '', values: {} },
    axes,
  );
}
